import { useState, useEffect } from "react";
import { getDoc, doc } from "firebase/firestore";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { AiFillCheckCircle, AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";
import { FaWhatsapp, FaPhone, FaBed, FaBath, FaRulerCombined, FaMapMarkerAlt, FaCalendarAlt, FaHandHoldingUsd, FaRupeeSign, FaPercentage, FaClock, FaBuilding, FaHome, FaLandmark, FaStore, FaWarehouse, FaTree, FaCar, FaShieldAlt, FaWifi, FaSwimmingPool, FaDumbbell, FaParking } from "react-icons/fa";
import { MdVerified, MdLocationOn } from "react-icons/md";
import { db } from "../firebase.config";
import ScaleSpinner from "../components/feedback/ScaleSpinner";
import { Helmet } from "react-helmet";
import { useColorModeValue, Alert, AlertIcon, AlertTitle, AlertDescription, Tooltip, Progress, Divider, Badge, Icon, Flex, Box as ChakraBox } from "@chakra-ui/react";

const PropertyDetails = () => {
  const [propertyListing, setPropertyListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [error, setError] = useState(null);
  const [showPaymentBreakdown, setShowPaymentBreakdown] = useState(false);
  const [showBedAndBath, setShowBedAndBath] = useState(false);

  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const param = location.state;

  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Function to check if property type should show bedrooms and bathrooms
  const shouldShowBedAndBath = () => {
    const propertyType = getPropertyType().toLowerCase();
    const typesWithBedBath = [
      'house', 'apartment', 'villa', 'condo', 'townhouse', 
      'residential', 'home', 'flat', 'bungalow', 'duplex',
      'penthouse', 'cottage', 'mansion', 'studio', 'lower portion',
      'upper portion', 'full house'
    ];
    const typesWithoutBedBath = [
      'land', 'plot', 'commercial', 'shop', 'office', 
      'warehouse', 'factory', 'building', 'agricultural',
      'farm', 'industrial', 'retail', 'restaurant', 'hotel',
      'godown', 'showroom', 'plaza'
    ];
    
    if (typesWithoutBedBath.some(type => propertyType.includes(type))) {
      return false;
    }
    
    if (typesWithBedBath.some(type => propertyType.includes(type))) {
      return true;
    }
    
    return propertyType.includes('residential');
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowFullScreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const fetchPropertyListing = async () => {
      try {
        const sellerId = searchParams.get("sellerId")?.trim();
        const type = searchParams.get("type")?.trim();
        const agentId = searchParams.get("agentId")?.trim();
        let docRef = null;

        if (type === "featured-property") {
          docRef = doc(db, "featured-properties", params.listingId);
        } else if (type === "featured-seller" || type === "featured-seller-listing") {
          if (sellerId && params.listingId) {
            docRef = doc(db, "Feature-Seller", sellerId, "listings", params.listingId);
          }
        } else if (type === "featured-agent") {
          docRef = doc(db, "properties", params.listingId);
        } else {
          docRef = doc(db, "properties", params.listingId);
        }

        if (!docRef) {
          docRef = doc(db, "properties", params.listingId);
        }

        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPropertyListing(data);
        } else {
          const alternativeCollections = ["properties", "listings", "featuredProperties"];
          for (const collectionName of alternativeCollections) {
            try {
              const altDocRef = doc(db, collectionName, params.listingId);
              const altDocSnap = await getDoc(altDocRef);
              if (altDocSnap.exists()) {
                setPropertyListing(altDocSnap.data());
                return;
              }
            } catch (altError) {
              continue;
            }
          }
          setError("Property listing not found");
        }
      } catch (error) {
        console.error("Error fetching property listing:", error);
        setError(`Failed to load property details`);
      } finally {
        setLoading(false);
      }
    };

    if (params.listingId) {
      fetchPropertyListing();
    } else {
      setError("No property ID provided");
      setLoading(false);
    }
  }, [params.listingId, param, searchParams]);

  const handleWhatsAppContact = () => {
    if (!propertyListing) return;

    let message = `*🏠 Property Inquiry from EstateGhar*\n\n`;
    message += `*Property:* ${getTitle()}\n`;
    message += `*Location:* ${getFullLocation()}\n`;
    message += `*Price:* ${formatPrice(displayPrice)}${isRent ? ' per month' : ''}\n`;
    message += `*Type:* ${isRent ? 'For Rent' : 'For Sale'}\n`;
    
    if (showBedAndBath && propertyListing?.bedrooms) {
      message += `*Bedrooms:* ${propertyListing.bedrooms}\n`;
    }
    if (showBedAndBath && propertyListing?.bathrooms) {
      message += `*Bathrooms:* ${propertyListing.bathrooms}\n`;
    }
    if (propertyListing?.area) {
      message += `*Area:* ${propertyListing.marla ? `${propertyListing.marla} Marla` : `${propertyListing.area} sq ft`}\n`;
    }
    
    if (hasInstallments) {
      message += `\n*💰 Easy Installment Plan Available*\n`;
      if (propertyListing?.downPayment) {
        message += `Down Payment: ${formatCurrency(propertyListing.downPayment)}\n`;
      }
      if (propertyListing?.monthlyPayment) {
        message += `Monthly: ${formatCurrency(propertyListing.monthlyPayment)}\n`;
      }
    }
    
    message += `\nIs this property still available? I'm interested in viewing it.`;

    const url = `https://wa.me/${getPhoneNumber()}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const handleCallContact = () => {
    const phone = getPhoneNumber();
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      alert("Phone number not available");
    }
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return "Price on request";
    
    const priceNum = parseInt(price);
    
    if (priceNum >= 10000000) {
      const crores = priceNum / 10000000;
      return `PKR ${crores.toFixed(2)} Crore`;
    }
    
    if (priceNum >= 100000) {
      const lakhs = priceNum / 100000;
      return `PKR ${lakhs.toFixed(2)} Lakh`;
    }
    
    return `PKR ${priceNum.toLocaleString()}`;
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return null;
    return `PKR ${amount.toLocaleString()}`;
  };

  const formatArea = (area) => {
    if (!area) return "N/A";
    return `${parseInt(area).toLocaleString()} sq ft`;
  };

  const getInstallmentTotal = () => {
    if (!propertyListing?.installmentAvailable) return null;
    const { downPayment, monthlyPayment, installmentDuration } = propertyListing;
    if (downPayment && monthlyPayment && installmentDuration) {
      return downPayment + (monthlyPayment * installmentDuration);
    }
    return null;
  };

  const getSavings = () => {
    if (propertyListing?.offer && propertyListing?.discountedPrice && propertyListing?.price) {
      return propertyListing.price - propertyListing.discountedPrice;
    }
    return null;
  };

  const getPropertyImages = () => {
    if (!propertyListing) return ["/placeholder-property.jpg"];
    
    return propertyListing.propertyImages || 
           propertyListing.images || 
           [propertyListing.thumbnail || "/placeholder-property.jpg"];
  };

  const getPropertyType = () => {
    return propertyListing?.propertyType || propertyListing?.type || "Property";
  };

  const getFullLocation = () => {
    const parts = [];
    if (propertyListing?.location) parts.push(propertyListing.location);
    if (propertyListing?.society) parts.push(propertyListing.society);
    if (propertyListing?.sector) parts.push(propertyListing.sector);
    if (propertyListing?.block) parts.push(`Block ${propertyListing.block}`);
    if (propertyListing?.city) parts.push(propertyListing.city);
    
    return parts.length > 0 ? parts.join(", ") : "Location not specified";
  };

  const getTitle = () => {
    return propertyListing?.title || propertyListing?.propertyTitle || `${getPropertyType()} for ${propertyListing?.type || 'sale'}`;
  };

  const getDescription = () => {
    return propertyListing?.description || "No description available.";
  };

  const getPhoneNumber = () => {
    return "923487172565";
  };

  const getAmenities = () => {
    if (!propertyListing) return [];
    
    const amenitiesString = propertyListing.amenities || 
                           propertyListing.features || 
                           "";
    
    if (!amenitiesString || typeof amenitiesString !== 'string') {
      return [];
    }
    
    return amenitiesString.split(',').map(item => item.trim()).filter(item => item.length > 0);
  };

  const getFeatures = () => {
    if (!propertyListing) return [];
    
    const featuresString = propertyListing.features || 
                          propertyListing.amenities || 
                          "";
    
    if (!featuresString || typeof featuresString !== 'string') {
      return [];
    }
    
    return featuresString.split(',').map(item => item.trim()).filter(item => item.length > 0);
  };

  const isRent = propertyListing?.type === "rent" || 
                 propertyListing?.listingType === "rent" || 
                 propertyListing?.status === "for-rent";

  const hasInstallments = propertyListing?.installmentAvailable === true;
  const installmentTotal = getInstallmentTotal();
  const savings = getSavings();
  const displayPrice = propertyListing?.offer && propertyListing?.discountedPrice 
    ? propertyListing.discountedPrice 
    : propertyListing?.price;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ScaleSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 max-w-md rounded-lg">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        <a href="/" className="mt-4 text-green-600 hover:underline">
          Return to homepage
        </a>
      </div>
    );
  }

  if (!propertyListing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 max-w-md rounded-lg">
          <p className="font-bold">No Property Found</p>
          <p>The requested property listing could not be found.</p>
        </div>
        <a href="/" className="mt-4 text-green-600 hover:underline">
          Browse other properties
        </a>
      </div>
    );
  }

  const propertyImages = getPropertyImages();
  const amenities = getAmenities();
  const features = getFeatures();
  const displayItems = isRent ? features : amenities;
  const showBedBath = shouldShowBedAndBath();

  // Get icon for property type
  const getPropertyTypeIcon = () => {
    const type = getPropertyType().toLowerCase();
    if (type.includes('house') || type.includes('portion') || type.includes('banglow')) return <FaHome className="text-green-600" size={24} />;
    if (type.includes('apartment') || type.includes('flat')) return <FaBuilding className="text-green-600" size={24} />;
    if (type.includes('plot') || type.includes('land')) return <FaTree className="text-green-600" size={24} />;
    if (type.includes('shop') || type.includes('store')) return <FaStore className="text-green-600" size={24} />;
    if (type.includes('office')) return <FaLandmark className="text-green-600" size={24} />;
    if (type.includes('warehouse') || type.includes('godown')) return <FaWarehouse className="text-green-600" size={24} />;
    return <FaBuilding className="text-green-600" size={24} />;
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-50 to-gray-100`}>
      <Helmet>
        <title>
          {getTitle()} in {getFullLocation()} | EstateGhar
        </title>
        <meta
          name="description"
          content={`View details for ${getPropertyType()} property for ${isRent ? 'rent' : 'sale'} in ${getFullLocation()}${showBedBath && propertyListing.bedrooms ? ` - ${propertyListing.bedrooms} bedrooms, ${propertyListing.bathrooms} bathrooms` : ''} at ${formatPrice(propertyListing.price)}${hasInstallments ? ' with easy installment plan available' : ''}`}
        />
      </Helmet>

      {/* Fullscreen Image Viewer */}
      {showFullScreen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center">
            <button
              className="absolute top-5 right-5 text-white text-4xl p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all z-10"
              onClick={() => setShowFullScreen(false)}
            >
              &times;
            </button>

            <button
              className="absolute left-5 text-white text-4xl p-3 hover:bg-white hover:bg-opacity-20 rounded-full transition-all z-10"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) =>
                  prev === 0 ? propertyImages.length - 1 : prev - 1
                );
              }}
            >
              <AiOutlineArrowLeft />
            </button>

            <img
              src={propertyImages[activeImageIndex]}
              alt="Fullscreen view"
              className="max-w-full max-h-full object-contain p-4"
              onClick={(e) => e.stopPropagation()}
            />

            <button
              className="absolute right-5 text-white text-4xl p-3 hover:bg-white hover:bg-opacity-20 rounded-full transition-all z-10"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) =>
                  prev === propertyImages.length - 1 ? 0 : prev + 1
                );
              }}
            >
              <AiOutlineArrowRight />
            </button>

            <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-4 py-2 rounded-full">
              {activeImageIndex + 1} / {propertyImages.length}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Image Gallery */}
        <div className="mb-8 rounded-2xl overflow-hidden shadow-2xl bg-white">
          <div className="relative h-64 sm:h-96 md:h-[500px] w-full bg-gray-100">
            <img
              src={propertyImages[activeImageIndex]}
              alt={getTitle()}
              className="w-full h-full object-cover cursor-pointer transition-transform duration-300 hover:scale-105"
              onClick={() => setShowFullScreen(true)}
              onError={(e) => {
                e.target.src = "/placeholder-property.jpg";
              }}
            />
            
            {/* Image counter badge */}
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
              {activeImageIndex + 1} / {propertyImages.length}
            </div>
            
            {/* Fullscreen button */}
            <button
              className="absolute bottom-4 right-4 bg-black bg-opacity-60 text-white p-2 rounded-full hover:bg-opacity-80 transition-all"
              onClick={() => setShowFullScreen(true)}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
              </svg>
            </button>
          </div>
          
          {/* Thumbnails */}
          {propertyImages.length > 1 && (
            <div className="grid grid-cols-5 sm:grid-cols-8 gap-1 p-2 bg-gray-50">
              {propertyImages.slice(0, 8).map((img, index) => (
                <div
                  key={index}
                  className={`aspect-square cursor-pointer rounded-lg overflow-hidden transition-all ${
                    index === activeImageIndex
                      ? "ring-2 ring-green-500 shadow-lg"
                      : "opacity-70 hover:opacity-100"
                  }`}
                  onClick={() => setActiveImageIndex(index)}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "/placeholder-property.jpg";
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title and Badges */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  {getPropertyTypeIcon()}
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                    {getTitle()}
                  </h1>
                  <Flex align="center" wrap="wrap" gap={2}>
                    <Badge colorScheme="green" px={3} py={1} borderRadius="full" fontSize="sm">
                      {isRent ? '🏠 For Rent' : '💰 For Sale'}
                    </Badge>
                    {propertyListing.featured && (
                      <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">
                        <MdVerified className="inline mr-1" /> Featured
                      </Badge>
                    )}
                    {hasInstallments && (
                      <Badge colorScheme="purple" px={3} py={1} borderRadius="full" fontSize="sm">
                        <FaHandHoldingUsd className="inline mr-1" /> Easy Installments
                      </Badge>
                    )}
                    {propertyListing.offer && (
                      <Badge colorScheme="red" px={3} py={1} borderRadius="full" fontSize="sm">
                        <FaPercentage className="inline mr-1" /> Special Offer
                      </Badge>
                    )}
                  </Flex>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl">
                <MdLocationOn className="text-green-600 mt-0.5 flex-shrink-0" size={20} />
                <div>
                  <p className="text-gray-700 font-medium">{getFullLocation()}</p>
                  {propertyListing?.nearbyLandmarks && (
                    <p className="text-sm text-gray-500 mt-1">
                      📍 Nearby: {propertyListing.nearbyLandmarks}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Price Section */}
            <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-2xl p-6 shadow-lg text-white">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-green-100 text-sm mb-1">Price</p>
                  <div className="flex items-baseline flex-wrap gap-2">
                    <span className="text-4xl font-bold">
                      {formatPrice(displayPrice)}
                    </span>
                    {isRent && (
                      <span className="text-green-100">/ month</span>
                    )}
                    {propertyListing.offer && propertyListing.price && propertyListing.discountedPrice && (
                      <span className="text-sm line-through text-green-200">
                        {formatPrice(propertyListing.price)}
                      </span>
                    )}
                  </div>
                  
                  {savings && savings > 0 && (
                    <div className="mt-2 text-sm bg-white bg-opacity-20 inline-block px-3 py-1 rounded-full">
                      💰 Save PKR {savings.toLocaleString()} ({Math.round((savings / propertyListing.price) * 100)}% off)
                    </div>
                  )}
                </div>
                
                {/* Single Contact Button Group */}
                <div className="flex gap-3">
                  <button
                    onClick={handleWhatsAppContact}
                    className="flex items-center gap-2 px-6 py-3 bg-white text-green-700 rounded-xl font-semibold hover:shadow-xl transition-all transform hover:scale-105"
                  >
                    <FaWhatsapp className="text-xl" />
                    WhatsApp
                  </button>
                  <button
                    onClick={handleCallContact}
                    className="flex items-center gap-2 px-6 py-3 bg-green-700 text-white rounded-xl font-semibold hover:bg-green-800 transition-all transform hover:scale-105"
                  >
                    <FaPhone />
                    Call
                  </button>
                </div>
              </div>
            </div>

            {/* Installment Plan Section */}
            {hasInstallments && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-emerald-100">
                <div 
                  className="p-6 cursor-pointer hover:bg-emerald-50 transition-colors"
                  onClick={() => setShowPaymentBreakdown(!showPaymentBreakdown)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-r from-emerald-500 to-green-500 p-3 rounded-xl">
                        <FaHandHoldingUsd className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">
                          Easy Installment Plan
                        </h3>
                        <p className="text-sm text-emerald-600">Zero Interest | Shariah Compliant</p>
                      </div>
                    </div>
                    <button className="text-emerald-600 font-semibold">
                      {showPaymentBreakdown ? "▲ Hide" : "▼ View Details"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {propertyListing.downPayment > 0 && (
                      <div className="text-center p-4 bg-emerald-50 rounded-xl">
                        <FaRupeeSign className="text-emerald-600 mx-auto mb-2" size={24} />
                        <p className="text-xs text-gray-500">Down Payment</p>
                        <p className="text-xl font-bold text-emerald-700">
                          {formatCurrency(propertyListing.downPayment)}
                        </p>
                      </div>
                    )}
                    {propertyListing.monthlyPayment > 0 && (
                      <div className="text-center p-4 bg-emerald-50 rounded-xl">
                        <FaRupeeSign className="text-emerald-600 mx-auto mb-2" size={24} />
                        <p className="text-xs text-gray-500">Monthly Installment</p>
                        <p className="text-xl font-bold text-emerald-700">
                          {formatCurrency(propertyListing.monthlyPayment)}
                        </p>
                      </div>
                    )}
                    {propertyListing.installmentDuration > 0 && (
                      <div className="text-center p-4 bg-emerald-50 rounded-xl">
                        <FaClock className="text-emerald-600 mx-auto mb-2" size={24} />
                        <p className="text-xs text-gray-500">Duration</p>
                        <p className="text-xl font-bold text-emerald-700">
                          {propertyListing.installmentDuration} Months
                        </p>
                      </div>
                    )}
                  </div>

                  {showPaymentBreakdown && (
                    <div className="mt-6 p-5 bg-gray-50 rounded-xl space-y-3 animate-fadeIn">
                      <h4 className="font-bold text-gray-800 text-lg">Payment Breakdown</h4>
                      
                      {installmentTotal && (
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-2">
                            <span>Payment Progress</span>
                            <span>{Math.round((propertyListing.downPayment / installmentTotal) * 100)}% at booking</span>
                          </div>
                          <Progress 
                            value={(propertyListing.downPayment / installmentTotal) * 100} 
                            size="lg" 
                            colorScheme="green" 
                            borderRadius="full"
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="flex justify-between p-3 bg-emerald-100 rounded-lg">
                          <span className="font-medium">Down Payment (at booking)</span>
                          <span className="font-bold text-emerald-700">
                            {formatCurrency(propertyListing.downPayment)}
                          </span>
                        </div>
                        <div className="flex justify-between p-3 bg-gray-100 rounded-lg">
                          <span className="font-medium">Monthly Installment × {propertyListing.installmentDuration}</span>
                          <span className="font-bold text-emerald-700">
                            {formatCurrency(propertyListing.monthlyPayment * propertyListing.installmentDuration)}
                          </span>
                        </div>
                        <Divider />
                        <div className="flex justify-between p-3 bg-gradient-to-r from-emerald-100 to-green-100 rounded-lg">
                          <span className="font-bold text-gray-800">Total Amount</span>
                          <span className="font-bold text-2xl text-emerald-800">
                            {formatCurrency(installmentTotal)}
                          </span>
                        </div>
                      </div>

                      <Alert status="info" borderRadius="lg" mt={4}>
                        <AlertIcon />
                        <ChakraBox>
                          <AlertTitle fontSize="sm">No Interest / Markup</AlertTitle>
                          <AlertDescription fontSize="xs">
                            Shariah-compliant, interest-free installment plan. Contact seller for agreement details.
                          </AlertDescription>
                        </ChakraBox>
                      </Alert>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Property Specifications */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-2xl font-bold mb-5 text-gray-800 border-b-2 border-green-500 pb-2 inline-block">
                Property Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {[
                  showBedBath && propertyListing.bedrooms && { 
                    icon: <FaBed className="text-green-600" size={18} />, 
                    label: "Bedrooms", 
                    value: propertyListing.bedrooms 
                  },
                  showBedBath && propertyListing.bathrooms && { 
                    icon: <FaBath className="text-green-600" size={18} />, 
                    label: "Bathrooms", 
                    value: propertyListing.bathrooms 
                  },
                  (propertyListing.area || propertyListing.marla) && { 
                    icon: <FaRulerCombined className="text-green-600" size={18} />, 
                    label: "Area", 
                    value: propertyListing.marla ? `${propertyListing.marla} Marla` : formatArea(propertyListing.area) 
                  },
                  propertyListing.kanal > 0 && { 
                    icon: <FaRulerCombined className="text-green-600" size={18} />, 
                    label: "Kanal", 
                    value: `${propertyListing.kanal} Kanal` 
                  },
                  { icon: <MdLocationOn className="text-green-600" size={18} />, label: "Location", value: getFullLocation() },
                  { icon: <FaBuilding className="text-green-600" size={18} />, label: "Property Type", value: getPropertyType() },
                  propertyListing.yearBuilt && { 
                    icon: <FaCalendarAlt className="text-green-600" size={18} />, 
                    label: "Year Built", 
                    value: propertyListing.yearBuilt 
                  },
                  propertyListing.plotDimensions && { 
                    icon: <FaRulerCombined className="text-green-600" size={18} />, 
                    label: "Plot Dimensions", 
                    value: propertyListing.plotDimensions 
                  },
                  propertyListing.plotNumber && { 
                    icon: <FaHome className="text-green-600" size={18} />, 
                    label: "Plot Number", 
                    value: propertyListing.plotNumber 
                  },
                  propertyListing.sector && { 
                    icon: <FaLandmark className="text-green-600" size={18} />, 
                    label: "Sector/Block", 
                    value: propertyListing.sector 
                  },
                ].filter(Boolean).map((spec, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:shadow-md transition-all"
                  >
                    <div className="text-green-600">{spec.icon}</div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide">{spec.label}</div>
                      <div className="font-semibold text-gray-800">{spec.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b-2 border-green-500 pb-2 inline-block">
                Description
              </h2>
              <p className="text-gray-700 leading-relaxed mt-4 whitespace-pre-wrap">
                {getDescription()}
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card - Single unified contact section */}
            <div className="bg-gradient-to-br from-green-600 to-teal-700 rounded-2xl p-6 shadow-xl sticky top-4 text-white">
              <div className="text-center mb-5">
                <div className="bg-white bg-opacity-20 rounded-full p-3 inline-block mb-3">
                  <FaWhatsapp className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold">Interested in this property?</h3>
                <p className="text-green-100 text-sm mt-1">Get quick response on WhatsApp or Call</p>
              </div>
              
              <Divider borderColor="whiteAlpha.300" my={4} />
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white bg-opacity-10 rounded-xl">
                  <span className="text-sm">📞 Phone Number</span>
                  <span className="font-mono font-semibold">{getPhoneNumber()}</span>
                </div>
                
                <button
                  onClick={handleWhatsAppContact}
                  className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-white text-green-700 rounded-xl font-bold hover:shadow-xl transition-all transform hover:scale-105"
                >
                  <FaWhatsapp size={22} />
                  Message on WhatsApp
                </button>
                
                <button
                  onClick={handleCallContact}
                  className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-green-700 text-white rounded-xl font-bold hover:bg-green-800 transition-all"
                >
                  <FaPhone />
                  Call Now
                </button>
              </div>
              
              <div className="mt-4 text-center text-green-100 text-xs">
                <p>Response time: Usually within minutes</p>
              </div>
            </div>

            {/* Features/Amenities */}
            {displayItems.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-gray-800 border-b-2 border-green-500 pb-2 inline-block">
                  {isRent ? "✨ Features" : "🏆 Amenities"}
                </h3>
                <div className="grid grid-cols-2 gap-2 mt-4 max-h-80 overflow-y-auto">
                  {displayItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <AiFillCheckCircle className="text-green-500 flex-shrink-0" size={14} />
                      <span className="text-sm text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Info */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-gray-800 border-b-2 border-green-500 pb-2 inline-block">
                ℹ️ Quick Info
              </h3>
              <div className="space-y-3 mt-4">
                <div className="flex justify-between p-2 border-b border-gray-100">
                  <span className="text-gray-500">Property ID:</span>
                  <span className="font-mono text-sm font-medium">{params.listingId.slice(-8)}</span>
                </div>
                <div className="flex justify-between p-2 border-b border-gray-100">
                  <span className="text-gray-500">Status:</span>
                  <Badge colorScheme="green" size="sm">Available</Badge>
                </div>
                <div className="flex justify-between p-2">
                  <span className="text-gray-500">Listed:</span>
                  <span className="text-sm">
                    {propertyListing.timestamp?.toDate?.().toLocaleDateString() || 
                     propertyListing.createdAt?.toDate?.().toLocaleDateString() || 
                     'Recently'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PropertyDetails;